// Iter 193 — 📲 Bannière « Installer l'application » (PWA).
// Android/Chrome : bouton natif (beforeinstallprompt). iPhone/iPad : mini-guide Safari.
// Iter 270 (Kamel — test iPhone) : la bannière peut être ROUVERTE à tout moment
// via l'événement `raav-open-install` (bouton « Installer l'app » du menu),
// même si elle a été fermée (« Plus tard ») — avec guide pas-à-pas complet.
import React, { useState, useEffect } from 'react';

const DISMISS_KEY = 'raav_pwa_dismiss';
const DISMISS_DAYS = 30;
const IS_IOS = /iphone|ipad|ipod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
// Iter 270b (Kamel) — Chrome sur iPhone (CriOS) : le bouton Partager est en HAUT
// à droite (pas en bas comme Safari) mais l'installation marche aussi (iOS 16.4+).
const IS_IOS_CHROME = IS_IOS && /crios/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');

export const InstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!standalone) setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    // Iter 270 — Réouverture manuelle depuis le menu (ignore le dismiss)
    const onOpen = () => {
      setManual(true);
      setShowIos(IS_IOS);
      setVisible(true);
    };
    window.addEventListener('raav-open-install', onOpen);
    let t;
    if (!standalone) {
      const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (Date.now() - last >= DISMISS_DAYS * 86400000 && IS_IOS) {
        t = setTimeout(() => { setShowIos(true); setVisible(true); }, 4000);
      }
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('raav-open-install', onOpen);
      if (t) clearTimeout(t);
    };
  }, []);

  const installer = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferred(null);
  };

  const fermer = () => {
    if (!manual) localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setManual(false);
  };

  if (!visible) return null;

  const Etape = ({ n, children }) => (
    <li className="flex items-start gap-2 mt-1.5">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white text-[#1E3A5F] text-[11px] font-bold flex items-center justify-center mt-0.5">{n}</span>
      <span className="text-xs text-gray-100 leading-snug">{children}</span>
    </li>
  );

  return (
    <div className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] lg:bottom-3 left-3 right-3 sm:left-auto sm:w-96 z-[9999] bg-[#1E3A5F] text-white rounded-2xl shadow-2xl p-4"
      data-testid="pwa-install-banner">
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="RAAV" className="w-11 h-11 rounded-xl flex-shrink-0" />
        <div className="flex-1">
          <p className="font-bold text-sm">📲 Installez notre application !</p>
          {showIos ? (
            IS_IOS_CHROME ? (
              <ol data-testid="pwa-ios-chrome-steps">
                <Etape n="1">Dans Chrome, appuyez sur le bouton <strong>Partager</strong> <span aria-hidden>⬆️</span> (le carré avec la flèche, <strong>en haut à droite</strong> à côté de la barre d'adresse)</Etape>
                <Etape n="2">Faites défiler et choisissez « <strong>Ajouter à l'écran d'accueil</strong> »</Etape>
                <Etape n="3">Appuyez sur <strong>Ajouter</strong> — l'icône apparaît sur votre écran 🎉</Etape>
                <li className="text-[10px] text-gray-300 mt-1.5 italic">Si l'option n'apparaît pas (ancien iPhone), ouvrez le site dans Safari.</li>
              </ol>
            ) : (
              <ol data-testid="pwa-ios-steps">
                <Etape n="1">Appuyez sur le bouton <strong>Partager</strong> <span aria-hidden>⬆️</span> (le carré avec la flèche, en bas au milieu de Safari)</Etape>
                <Etape n="2">Faites défiler et choisissez « <strong>Sur l'écran d'accueil</strong> »</Etape>
                <Etape n="3">Appuyez sur <strong>Ajouter</strong> — l'icône apparaît sur votre écran 🎉</Etape>
              </ol>
            )
          ) : deferred ? (
            <p className="text-xs text-gray-200 mt-1">
              Votre espace, vos voyages et vos paiements directement sur votre écran d'accueil.
            </p>
          ) : (
            <ol data-testid="pwa-android-steps">
              <Etape n="1">Appuyez sur les <strong>3 petits points ⋮</strong> en haut à droite de Chrome</Etape>
              <Etape n="2">Choisissez « <strong>Installer l'application</strong> » (ou « <strong>Ajouter à l'écran d'accueil</strong> »)</Etape>
              <Etape n="3">Confirmez — l'icône apparaît sur votre écran 🎉</Etape>
            </ol>
          )}
          <a href="/installer-l-appli" className="inline-block mt-1 text-xs underline text-[#79c9ea] font-semibold" data-testid="pwa-guide-illustre">📖 Voir le guide illustré pas à pas (captures d'écran)</a>
          <div className="flex gap-2 mt-2.5">
            {!showIos && deferred && (
              <button onClick={installer} data-testid="pwa-install-btn"
                className="text-xs font-bold px-3 py-1.5 bg-white text-[#1E3A5F] rounded-lg hover:bg-gray-100">
                Installer
              </button>
            )}
            <button onClick={fermer} data-testid="pwa-dismiss-btn"
              className="text-xs font-bold px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20">
              {manual ? 'Fermer' : 'Plus tard'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallBanner;
